import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import * as FaIcons from 'react-icons/fa';
import SafeIcon from '../common/SafeIcon';
import QuickActionBar from './QuickActionBar';
import UserProfile from './UserProfile';

const { 
  FiHome, 
  FiMenu, 
  FiX, 
  FiDroplet, 
  FiCoffee, 
  FiTrendingUp, 
  FiCalendar, 
  FiTool, 
  FiCamera, 
  FiBell,
  FiUser 
} = FiIcons;

const { FaFish } = FaIcons;

const Layout = ({ children }) => {
  const location = useLocation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const toggleMenu = () => setIsMenuOpen(!isMenuOpen);

  const navigationItems = [
    { path: '/', label: 'Dashboard', icon: FiHome },
    { path: '/water-test', label: 'Water Test', icon: FiDroplet },
    { path: '/feeding', label: 'Feeding', icon: FiCoffee },
    { path: '/growth', label: 'Growth', icon: FiTrendingUp },
    { path: '/maintenance', label: 'Maintenance', icon: FiTool },
    { path: '/timeline', label: 'Timeline', icon: FiCalendar },
    { path: '/images', label: 'Images', icon: FiCamera }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200 sticky top-0 z-40">
        <div className="px-4 py-3">
          <div className="flex items-center justify-between">
            {/* Logo and Brand */}
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 bg-aqua-500 rounded-lg flex items-center justify-center">
                <SafeIcon icon={FaFish} className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="text-lg font-semibold text-gray-900">Tilapia Manager</h1>
                <p className="text-xs text-gray-500 hidden sm:block">Professional Aquaculture System</p>
              </div>
            </div>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center space-x-1">
              {navigationItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`flex items-center space-x-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                    location.pathname === item.path
                      ? 'bg-aqua-100 text-aqua-700'
                      : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                  }`}
                >
                  <SafeIcon icon={item.icon} className="w-4 h-4" />
                  <span>{item.label}</span>
                </Link>
              ))}
            </nav>

            {/* Desktop Actions */}
            <div className="hidden lg:flex items-center space-x-3">
              <button className="relative p-2 rounded-lg hover:bg-gray-100 transition-colors">
                <SafeIcon icon={FiBell} className="w-5 h-5 text-gray-600" />
                <span className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full"></span>
              </button>
              <UserProfile />
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={toggleMenu}
              className="p-2 rounded-lg hover:bg-gray-100 transition-colors lg:hidden"
            >
              <SafeIcon icon={isMenuOpen ? FiX : FiMenu} className="w-6 h-6 text-gray-600" />
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Menu Overlay */}
      <AnimatePresence>
        {isMenuOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 z-50 lg:hidden"
            onClick={toggleMenu}
          >
            <motion.div
              initial={{ x: '-100%' }}
              animate={{ x: 0 }}
              exit={{ x: '-100%' }}
              transition={{ type: 'tween', duration: 0.3 }}
              className="w-80 h-full bg-white shadow-xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-4 border-b border-gray-200">
                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 bg-aqua-500 rounded-lg flex items-center justify-center">
                    <SafeIcon icon={FaFish} className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h2 className="text-lg font-semibold text-gray-900">Navigation</h2>
                    <p className="text-xs text-gray-500">Professional Aquaculture System</p>
                  </div>
                </div>
              </div>
              <nav className="p-4">
                {/* User Profile in Mobile */}
                <div className="mb-4 p-3 bg-aqua-50 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-aqua-500 rounded-full flex items-center justify-center">
                      <SafeIcon icon={FiUser} className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-900">Farm Manager</p>
                      <p className="text-xs text-gray-500">manager@tilapiafarm.com</p>
                    </div>
                  </div>
                </div>

                {/* Tips Section in Menu */}
                <div className="mb-4 p-3 bg-amber-50 rounded-lg">
                  <h3 className="text-sm font-medium text-amber-800">Quick Tips</h3>
                  <ul className="mt-2 text-sm text-amber-700 space-y-2">
                    <li>• Test water quality daily</li>
                    <li>• Monitor feeding response</li>
                    <li>• Check temperature twice daily</li>
                    <li>• Record any unusual behavior</li>
                  </ul>
                </div>
                <div className="space-y-2">
                  {navigationItems.map((item) => (
                    <Link
                      key={item.path}
                      to={item.path}
                      onClick={toggleMenu}
                      className={`flex items-center space-x-3 px-3 py-2 rounded-lg transition-colors ${
                        location.pathname === item.path
                          ? 'bg-aqua-100 text-aqua-700'
                          : 'text-gray-600 hover:bg-gray-100'
                      }`}
                    >
                      <SafeIcon icon={item.icon} className="w-5 h-5" />
                      <span className="font-medium">{item.label}</span>
                    </Link>
                  ))}
                </div>
              </nav>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Main Content */}
      <main className="pb-20 lg:pb-4">
        {children}
      </main>

      {/* Quick Action Bar - Only on Mobile */}
      <div className="lg:hidden">
        <QuickActionBar />
      </div>
    </div>
  );
};

export default Layout;